import { useState, useEffect } from 'react';
import SvgArrowLeft from 'src/components/icons/arrow-left-v2-2x';
import SvgArrowRight from 'src/components/icons/arrow-right-v2';
import type { ListType } from 'src/service/creation';
import type { RepplResponse } from 'src/app/reppl/service';
import { useTranslation } from 'react-i18next';
import Episode from 'src/components/common/card/episode';

interface RecentEpisodesProps extends Pick<RepplResponse, 'reppl'> {
  episodes: ListType[];
  isLoaded: boolean;
  episodesMostEnthused: ListType[];
  isOwner: boolean;
}

const RecentEpisodes = ({
  episodes,
  isLoaded,
  episodesMostEnthused,
  isOwner,
}: RecentEpisodesProps) => {
  const { t } = useTranslation();

  const [currentTab, setTab] = useState('enthused');
  const [data, setData] = useState(episodesMostEnthused);
  const [translateX, setTranslateX] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [rightLock, setRightLock] = useState(true);
  const [leftLock, setLeftLock] = useState(false);

  useEffect(() => {
    setData(episodesMostEnthused);
    if (episodesMostEnthused.length > benchmark) {
      setLeftLock(false);
    }
  }, [episodesMostEnthused]);

  const benchmark = 4;

  const offest = (direction) => {
    if (episodes.length <= benchmark) return;
    if (direction === 'right' && rightLock) return;
    if (direction === 'left' && leftLock) return;

    const offect = direction === 'left' ? -1 : 1;
    const x = 324 * offect + translateX;
    setTranslateX(x);
    const nextIndex = currentIndex - offect;
    let right = false;
    let left = false;
    if (nextIndex === 0) {
      right = true;
    } else if (nextIndex + benchmark >= episodes.length) {
      left = true;
    }
    setRightLock(right);
    setLeftLock(left);
    setCurrentIndex(nextIndex);
  };

  if (!isLoaded) {
    return (
      <div className="animate-pulse">
        <div className="w-40 h-6 skeleton-line my-6" />
        <div className="pb-2 flex flex-nowrap">
          <div className="mr-6 episode-card skeleton-line" />
          <div className="mr-6 episode-card skeleton-line" />
          <div className="mr-6 episode-card skeleton-line" />
          <div className="episode-card skeleton-line" />
        </div>
      </div>
    );
  }

  if (isLoaded && data.length < benchmark && !isOwner) {
    return null;
  }

  const tabHandle = (tabName) => {
    setTab(tabName);
    if (tabName === 'enthused') setData(episodesMostEnthused);
    else setData(episodes);
    setTranslateX(0);
    setCurrentIndex(0);
    setRightLock(true);
    const left = !(episodesMostEnthused.length > benchmark);
    setLeftLock(left);
  };

  return (
    <div className="px-4 reppl-container-width mt-10 md:px-6 md:my-auto">
      <header className="text-gray-500 font-medium font-sm md:text-xl mb-3 md:mb-6">
        <span
          aria-hidden="true"
          onClick={() => {
            tabHandle('enthused');
          }}
          className={`cursor-pointer ${
            currentTab === 'enthused' ? 'text-black' : ''
          }`}
        >
          {t('Most enthused')}
        </span>
        <span className="px-4 text-gray-300">|</span>
        <span
          aria-hidden="true"
          onClick={() => {
            tabHandle('update');
          }}
          className={`cursor-pointer ${
            currentTab === 'update' ? 'text-black' : ''
          }`}
        >
          {t('Latest updates')}
        </span>
      </header>
      <div className="relative">
        <div className="overflow-auto pb-2">
          <div
            className={`flex flex-nowrap explore-creations ${
              data.length < 4 ? 'section-empty-wrap' : ''
            }`}
            style={{ transform: `translateX(${translateX}px)` }}
          >
            {data.map((episode) => {
              return (
                <Episode
                  data={episode}
                  key={episode.contentID}
                  className="mr-6"
                />
              );
            })}
            {data.length < 4 && isOwner && (
              <div
                className={`flex-1 section-empty rounded-md flex items-center justify-center ${
                  data.length === 0 ? 'h-48' : ''
                }`}
              >
                <p className="text-center flex-1">
                  This section is not yet visible to your visitors.
                  <br />
                  Add 4 or more episodes to activate this section.
                </p>
              </div>
            )}
          </div>
          <div
            aria-hidden="true"
            onClick={() => {
              offest('right');
            }}
            className={`${
              rightLock ? 'text-gray-300' : 'text-gray-900'
            } hidden bg-white top-20 border border-gray-300 absolute z-10 -left-5 rounded-full text-sm p-3 cursor-pointer md:block`}
          >
            <SvgArrowLeft />
          </div>

          <div
            aria-hidden="true"
            onClick={() => {
              offest('left');
            }}
            className={`${
              leftLock ? 'text-gray-300' : 'text-gray-900'
            } hidden bg-white top-20 border border-gray-300 absolute z-10 -right-5 rounded-full text-sm p-3 cursor-pointer md:block`}
          >
            <SvgArrowRight />
          </div>
        </div>
      </div>
    </div>
  );
};
export default RecentEpisodes;
